// @flow
/* eslint-env browser */

import url from 'url';

import * as React from 'react';

import type { User } from '@gandi/authentication';
import type { Organization } from '@gandi/redux-sharing';
import { Button, Svg } from '@gandi/ui';
import { useTranslator } from '@gandi/translate';

import type { EnhancedPublicUrls, LayoutUrls } from '../../../types';

import styles from './Chat.scss';

export type StateProps = {|
  assetPath: string => string,
  locale: string,
  personalDetails: Organization,
  publicUrls: {| ...LayoutUrls, ...EnhancedPublicUrls |},
  sharingDetails: Organization,
  userInfos: ?User,
  hasParamUrlLiveChat: boolean,
|};

export type Props = StateProps;

export default function Chat({
  assetPath,
  locale,
  personalDetails,
  publicUrls,
  sharingDetails,
  userInfos,
  hasParamUrlLiveChat,
}: Props): React.Element<'div'> | boolean {
  const translator = useTranslator();
  const { __ } = translator;
  const [isClosedChat, setIsClosedChat] = React.useState(!hasParamUrlLiveChat);
  const [accepteCookie, setAccepCookie] = React.useState(true);

  function initChat(closeButton?: boolean) {
    if (typeof window !== 'undefined') {
      /* eslint-disable */
      // prettier-ignore
      window.$zopim||(function(d: any,s: any){
        // $FlowFixMe
        var z: ((c: any) => void)=window.$zopim=function(c){z._.push(c)},$=z.s=
        // $FlowFixMe
        d.createElement(s),e=d.getElementsByTagName(s)[0];z.set=function(o){z.set.
        // $FlowFixMe
        _.push(o)};z._=[];z.set._=[];$.async=!0;$.setAttribute("charset","utf-8");
        // $FlowFixMe
        $.src="https://v2.zopim.com/?DRL49mDICZUfknExghxjoOwO891X6ZN8";z.t=+new Date;
        $.type="text/javascript";
        if (e && e.parentNode) {
            e.parentNode.insertBefore($, e);
        }
        })(document, 'script');
      /* eslint-enable */
      const languageMap = {
        'zh-hans': 'zh_CN',
        'zh-hant': 'zh_TW',
      };
      const language = locale in languageMap ? languageMap[locale] : locale;

      // Set user info.
      window.$zopim(() => {
        const { livechat } = window.$zopim;

        if (userInfos && (userInfos.username || personalDetails.name)) {
          const fullName = `${userInfos.username} (${
            personalDetails.firstname || '-'
          } ${personalDetails.lastname || '-'})`;

          livechat.set({
            email: personalDetails.email || userInfos.email,
            language,
            name: fullName,
            phone: personalDetails.phone,
          });
          if (sharingDetails.orgname) {
            livechat.setNotes(
              `${fullName}) - \n${sharingDetails.name} (${sharingDetails.orgname})`
            );
          } else {
            livechat.setNotes(`${fullName}) - \n${sharingDetails.name}`);
          }

          if ('roles' in userInfos) {
            livechat.addTags(...userInfos.roles);
          }
        } else {
          livechat.set({
            language,
          });
        }

        // Set localization strings.
        livechat.setGreetings({
          online: __('Chat with us'),
          offline: __('All our agents are offline'),
        });
        livechat.prechatForm.setGreetings(
          __('Ask your question to the support team.')
        );

        // Set theme and appearance.
        livechat.badge.setImage(
          url.resolve(
            publicUrls.support || '',
            assetPath('img/chat_button.png')
          )
        );
        livechat.concierge.setAvatar(
          url.resolve(publicUrls.support || '', assetPath('img/gandi.png'))
        );

        livechat.theme.setColors({
          badge: '#11AAAA',
          primary: '#0d0b30', // $color-midnight-dark to be a bit darker than the header
        });
        livechat.theme.reload();

        if (!closeButton) {
          livechat.button.setHideWhenOffline(false);
          livechat.button.show();

          // Set button/badge/window behavior.
          livechat.setOnConnected(() => {
            livechat.setOnStatus((status: 'online' | 'away' | 'offline') => {
              const isChatting = livechat.isChatting();
              const isOpen = livechat.window.getDisplay();

              if (status === 'online') {
                if (!isChatting && !isOpen && hasParamUrlLiveChat) {
                  livechat.button.hide();
                  livechat.badge.show();
                } else {
                  livechat.button.show();
                }
              } else if (!isChatting) {
                livechat.button.show();
              }
            });
          });
        } else {
          // Add the cookie consent
          livechat.cookieLaw.comply();

          // Hide the button to open the chat
          livechat.button.hide();
          livechat.window.show();
        }

        livechat.window.onShow(() => {
          // Allow showing the window even when offline.
          livechat.window.show();

          // Don't actually hide the button when offline;
          // this is a workaround to disable the offlineForm when all support agents
          // are offline.
          //
          // Opening the livechat window will just display an explanation
          // instead of the form allowing to send a message,
          // as they should use the "contact support" form instead.
          window.setTimeout(() => {
            livechat.button.setHideWhenOffline(true);
          }, 0);
        });

        livechat.window.onHide(() => {
          window.setTimeout(() => {
            // But allow displaying the button so the "All our agents are offline"
            // text is shown; otherwise nothing would be shown at all.
            livechat.button.setHideWhenOffline(false);
            livechat.button.show();
          }, 0);
        });
      });
    }
  }

  React.useEffect(() => {
    if (document.cookie) {
      const cookies = document.cookie.split(';').reduce((acc, cookie) => {
        acc[cookie.split('=')[0].trim()] = cookie.split('=')[1].trim();
        return acc;
      }, ({}: { [string]: string }));

      // search if cookie zopim
      // eslint-disable-next-line no-underscore-dangle
      if (cookies.__zlcprivacy === '1') {
        return initChat();
      }
    }

    return setAccepCookie(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAccepteCookie = () => {
    // Strip the subdomain, so the cookie can also be seen on www and shop.
    const host = window.location.hostname.replace(/.+?\./, '');
    document.cookie = `__zlcprivacy=1;path=/;max-age=31536000;samesite=lax;domain=${host}`;
    initChat(true);
    setAccepCookie(true);
  };

  const ppUrl = sharingDetails.bu_id
    ? `${publicUrls.v5_prefix}/${locale}/contracts/pp?bu_id=${sharingDetails.bu_id}`
    : `${publicUrls.v5_prefix}/${locale}/contracts/pp`;

  return (
    !accepteCookie && (
      <div className={styles.root}>
        {
          /* eslint-disable @gandi/gandi/no-global-element */
          isClosedChat ? (
            <button
              type="button"
              className={styles.closed}
              onClick={() => setIsClosedChat(false)}
            >
              <div className={styles.containerIcon}>
                <svg
                  width="17"
                  height="17"
                  viewBox="0 0 17 17"
                  className={styles.icon}
                >
                  <path
                    fill="#FFF"
                    fillRule="evenodd"
                    d="M4.5,-3.55271368e-15 L12.5,-3.55271368e-15 C14.9852814,-4.00925246e-15 17,2.01471863 17,4.5 L17,8.25 C17,10.7352814 14.9852814,12.75 12.5,12.75 L8.92904948,12.75 L8.92904948,12.75 L5.43195191,16.599851 L5.43195191,12.75 L4.5,12.75 C2.01471863,12.75 1.40804583e-15,10.7352814 0,8.25 L0,4.5 C-3.04359188e-16,2.01471863 2.01471863,-3.0961749e-15 4.5,-3.55271368e-15 Z"
                  />
                </svg>
              </div>
              <div className={styles.text}>{__('Chat with us')}</div>
            </button>
          ) : (
            <div className={styles.opened}>
              <div className={styles.containerTop}>
                <p className={styles.openedTitle}>
                  {__('The chat tool requires accepting cookies.')}
                </p>
                <button
                  type="button"
                  onClick={() => setIsClosedChat(true)}
                  className={styles.openedClose}
                >
                  <Svg
                    name="cross"
                    theme="green"
                    style={{ width: 19, height: 'auto' }}
                  />
                </button>
              </div>

              <div className={styles.openedContainer}>
                <p
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={__(
                    'The chat tool requires cookies to function properly. ' +
                      'You can see the details of the cookies used in ' +
                      '<a href="%(pp_url)s">our privacy and cookie use policy</a>.',
                    {
                      pp_url: ppUrl,
                      withHTML: true,
                    }
                  )}
                  className={styles.openedInfo}
                />
                <p className={styles.openedInfo}>
                  {__(
                    `Accept them to continue or use our support form ` +
                      'if you refuse them.'
                  )}
                </p>
              </div>

              <div className={styles.bottom}>
                <Button type="button" small onClick={handleAccepteCookie}>
                  {__('Accept')}
                </Button>
                <a
                  href="#contact-support-form"
                  onClick={() => setIsClosedChat(true)}
                >
                  {__('Use form instead')}
                </a>
              </div>
            </div>
          )
          /* eslint-enable */
        }
      </div>
    )
  );
}
